import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CheckCircle, Policy } from "@mui/icons-material";
import { acceptConsent } from "../../store/slices/authSlice";
import axiosInstance from "../../services/api";

const CONSENT_CHECKBOXES = [
  {
    key: "dataUse",
    label:
      "I agree that InstituteERP may collect, process, and use my personal, academic, attendance, examination, assignment, timetable, fee, and related educational information to provide and manage institute services.",
  },
  {
    key: "notifications",
    label:
      "I agree to receive notifications, emails, and institute communications, and to allow required device permissions (such as camera and location) when necessary for features like attendance verification, QR/OTP attendance, and other authorized ERP services.",
  },
  {
    key: "storageSharing",
    label:
      "I understand and agree that my information may be securely stored, processed, and shared with my institution and trusted service providers only as necessary to operate InstituteERP, including authentication, messaging, file storage, online payments, and other platform services, in accordance with the Privacy Policy.",
  },
];

const INITIAL_CONSENT_CHECKS = Object.fromEntries(
  CONSENT_CHECKBOXES.map(({ key }) => [key, false])
);

const ConsentForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, activeConsentForm, consentMessage } = useSelector((state) => state.auth);
  const [consentChecks, setConsentChecks] = useState(INITIAL_CONSENT_CHECKS);
  const [error, setError] = useState(null);
  const [consentData, setConsentData] = useState(null);
  const [loadingConsent, setLoadingConsent] = useState(true);

  // Memoize the fetch function to avoid recreating it
  const loadConsentForm = useCallback(async () => {
    let mounted = true;

    try {
      // Try to get all active consent forms
      const response = await axiosInstance.get("/web/auth/consent-forms");
      
      if (mounted && response.data?.data) {
        const forms = Array.isArray(response.data.data) ? response.data.data : [];

        let form = activeConsentForm?.uuid
          ? forms.find((f) => f.uuid === activeConsentForm.uuid)
          : null;
        if (!form) {
          form =
            forms.find((f) => f.type === "privacy_policy") ||
            forms.find(
              (f) =>
                f.type === "terms_of_service" ||
                f.type === "terms_and_conditions" ||
                f.type === "termsAndConditions"
            ) ||
            forms.find((f) => f.type === "data_collection") ||
            forms[0];
        }
        
        if (form) {
          setConsentData(form);
        } else {
          setError("No consent form available. Please contact administrator.");
        }
      }
    } catch (err) {
      if (mounted) {
        console.error("Error loading consent form:", err);
        setError("Failed to load consent form. Please refresh the page.");
      }
    } finally {
      if (mounted) {
        setLoadingConsent(false);
      }
    }

    return () => {
      mounted = false;
    };
  }, [activeConsentForm?.uuid]);

  // Fetch consent form once on mount
  useEffect(() => {
    loadConsentForm();
  }, [loadConsentForm]);

  const allConsentChecked = useMemo(
    () => CONSENT_CHECKBOXES.every(({ key }) => consentChecks[key]),
    [consentChecks]
  );

  const handleAccept = useCallback(async () => {
    if (!allConsentChecked) {
      setError("Please check all three consent statements to continue.");
      return;
    }

    try {
      // Pass the consent form UUID so the backend knows which form was accepted
      const formUuid = consentData?.uuid || activeConsentForm?.uuid;
      const result = await dispatch(acceptConsent(formUuid));
      // Check if the action was fulfilled (successful)
      if (acceptConsent.fulfilled.match(result)) {
        // Wait a brief moment for state to update, then navigate
        // The ProtectedRoute will handle the redirect based on consentRequired state
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 100);
      } else if (acceptConsent.rejected.match(result)) {
        setError(result.payload || "Failed to accept consent. Please try again.");
      }
    } catch (err) {
      console.error('Error accepting consent:', err);
      setError("An error occurred. Please try again.");
    }
  }, [allConsentChecked, dispatch, navigate, consentData?.uuid, activeConsentForm?.uuid]);

  const handleCheckboxChange = useCallback((key) => (e) => {
    setConsentChecks((prev) => ({ ...prev, [key]: e.target.checked }));
    setError(null);
  }, []);

  // Memoize content rendering
  const contentComponent = useMemo(() => {
    if (loadingConsent) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (consentData) {
      return (
        <>
          <Box
            sx={{
              maxHeight: 400,
              overflowY: "auto",
              p: 3,
              bgcolor: "grey.50",
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
              }}
            >
              {consentData.content}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Please confirm each of the following to continue:
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {CONSENT_CHECKBOXES.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                sx={{ alignItems: "flex-start", m: 0 }}
                control={
                  <Checkbox
                    checked={consentChecks[key]}
                    onChange={handleCheckboxChange(key)}
                    color="primary"
                    sx={{ pt: 0.5 }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ pt: 0.75 }}>
                    {label}
                  </Typography>
                }
              />
            ))}
          </Box>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleAccept}
              disabled={loading || !allConsentChecked}
              startIcon={
                loading ? <CircularProgress size={20} /> : <CheckCircle />
              }
              sx={{ px: 6, py: 1.5 }}
            >
              {loading ? "Processing..." : "Accept & Continue"}
            </Button>
          </Box>
        </>
      );
    }

    return (
      <Alert severity="error">
        Unable to load consent form. Please refresh the page.
      </Alert>
    );
  }, [
    loadingConsent,
    consentData,
    consentChecks,
    handleCheckboxChange,
    loading,
    handleAccept,
    allConsentChecked,
  ]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
          {/* Header */}
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "white",
              p: 4,
              textAlign: "center",
            }}
          >
            <Policy sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" fontWeight={600}>
              {consentData?.title || "Terms & Conditions"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              {consentData?.summary || "Please read and accept our terms to continue"}
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            {(consentMessage || activeConsentForm?.title) && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {consentMessage ||
                  `Please review and accept "${activeConsentForm.title}" to continue using the app. You are still signed in.`}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {contentComponent}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ConsentForm;
